import React, { useState, useEffect, useCallback } from 'react';
import { jobTimelineAPI } from '../services/api';

const EMPTY_FORM = {
  id: null,
  job_title: '',
  client: '',
  vendor: '',
  technology: '',
  start_date: '',
  end_date: '',
  is_current: false,
};

export default function JobTimeline() {
  const [entries, setEntries]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState('');
  const [deletingId, setDeletingId]   = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await jobTimelineAPI.getMyTimeline();
      const data = res.data?.entries || res.data || [];
      // Sort by start_date DESC
      data.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
      setEntries(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load timeline.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openAdd = () => {
    setEditingEntry(null);
    setForm(EMPTY_FORM);
    setSaveError('');
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    setForm({
      id: entry.id,
      job_title: entry.job_title || '',
      client: entry.client || '',
      vendor: entry.vendor || '',
      technology: entry.technology || '',
      start_date: entry.start_date ? entry.start_date.slice(0, 10) : '',
      end_date: entry.end_date ? entry.end_date.slice(0, 10) : '',
      is_current: !!entry.is_current,
    });
    setSaveError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingEntry(null);
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'is_current' && checked ? { end_date: '' } : {}),
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.job_title.trim()) {
      setSaveError('Job title is required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        ...form,
        is_current: form.is_current ? 1 : 0,
        end_date: form.is_current ? null : form.end_date || null,
      };
      await jobTimelineAPI.saveEntry(payload);
      await fetchEntries();
      closeForm();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save entry.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete the entry "${entry.job_title}"? This cannot be undone.`)) return;
    setDeletingId(entry.id);
    try {
      await jobTimelineAPI.deleteEntry({ id: entry.id });
      await fetchEntries();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete entry.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Work History</h2>
          <small className="text-muted">Your job timeline and employment history</small>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <i className="bi bi-plus-lg me-2" />Add Entry
        </button>
      </div>

      {/* Inline Form */}
      {showForm && (
        <div className="card mb-4 border-primary">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <span>
              <i className="bi bi-briefcase me-2" />
              {editingEntry ? 'Edit Job Entry' : 'Add Job Entry'}
            </span>
            <button className="btn btn-sm btn-outline-light" onClick={closeForm} disabled={saving}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
          <div className="card-body">
            {saveError && <div className="alert alert-danger py-2">{saveError}</div>}
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Job Title <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" name="job_title" value={form.job_title}
                    onChange={handleChange} placeholder="e.g. Software Engineer" required />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Client</label>
                  <input type="text" className="form-control" name="client" value={form.client}
                    onChange={handleChange} placeholder="Client / End client" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Vendor</label>
                  <input type="text" className="form-control" name="vendor" value={form.vendor}
                    onChange={handleChange} placeholder="Staffing / consulting vendor" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Technology</label>
                  <input type="text" className="form-control" name="technology" value={form.technology}
                    onChange={handleChange} placeholder="e.g. React, Java, AWS" />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Start Date</label>
                  <input type="date" className="form-control" name="start_date" value={form.start_date}
                    onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">End Date</label>
                  <input type="date" className="form-control" name="end_date" value={form.end_date}
                    onChange={handleChange} disabled={form.is_current} />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check mb-2">
                    <input className="form-check-input" type="checkbox" id="isCurrent"
                      name="is_current" checked={form.is_current} onChange={handleChange} />
                    <label className="form-check-label fw-semibold" htmlFor="isCurrent">
                      Currently working here
                    </label>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 mt-4">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2" />Saving…</>
                    : <><i className="bi bi-check-lg me-2" />{editingEntry ? 'Update Entry' : 'Add Entry'}</>}
                </button>
                <button type="button" className="btn btn-outline-secondary" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="card">
        <div className="card-header">
          <h5 className="mb-0"><i className="bi bi-clock-history me-2" />Timeline</h5>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" />
              <div className="mt-2 text-muted">Loading timeline…</div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : entries.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-briefcase fs-1 d-block mb-2" />
              No job entries yet. Click <strong>Add Entry</strong> to build your work history.
            </div>
          ) : (
            <div className="timeline-container">
              {entries.map((entry, idx) => (
                <div key={entry.id} className="d-flex gap-3 mb-4 position-relative">
                  {/* Dot + vertical line */}
                  <div className="d-flex flex-column align-items-center" style={{ minWidth: 32 }}>
                    <div
                      className={`rounded-circle border border-2 d-flex align-items-center justify-content-center ${entry.is_current ? 'bg-success border-success text-white' : 'bg-white border-primary text-primary'}`}
                      style={{ width: 32, height: 32, flexShrink: 0, zIndex: 1 }}
                    >
                      <i className={`bi ${entry.is_current ? 'bi-briefcase-fill' : 'bi-briefcase'}`} style={{ fontSize: '.8rem' }} />
                    </div>
                    {idx < entries.length - 1 && (
                      <div className="bg-secondary opacity-25" style={{ width: 2, flex: 1, minHeight: 24 }} />
                    )}
                  </div>

                  {/* Card */}
                  <div className="card flex-grow-1 shadow-sm">
                    <div className="card-body py-3">
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                        <div>
                          <h6 className="mb-1 fw-bold">
                            {entry.job_title}
                            {entry.is_current && (
                              <span className="badge bg-success ms-2 align-middle" style={{ fontSize: '.7rem' }}>Current</span>
                            )}
                          </h6>
                          <div className="text-muted small mb-2">
                            <i className="bi bi-calendar3 me-1" />
                            {formatDate(entry.start_date)}
                            {' — '}
                            {entry.is_current ? <span className="text-success fw-semibold">Present</span> : formatDate(entry.end_date) || 'N/A'}
                          </div>
                          <div className="d-flex flex-wrap gap-3 small">
                            {entry.client && (
                              <span><i className="bi bi-building me-1 text-muted" /><strong>Client:</strong> {entry.client}</span>
                            )}
                            {entry.vendor && (
                              <span><i className="bi bi-shop me-1 text-muted" /><strong>Vendor:</strong> {entry.vendor}</span>
                            )}
                            {entry.technology && (
                              <span><i className="bi bi-code-slash me-1 text-muted" /><strong>Tech:</strong> {entry.technology}</span>
                            )}
                          </div>
                        </div>
                        <div className="d-flex gap-2 flex-shrink-0">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(entry)}
                            disabled={deletingId === entry.id}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(entry)}
                            disabled={deletingId === entry.id}>
                            {deletingId === entry.id
                              ? <span className="spinner-border spinner-border-sm" />
                              : <i className="bi bi-trash" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
